import type { ComuneFull, ComuneLight, ComuneIndex, LightIndex } from './types';

export function buildFullIndex(comuni: ComuneFull[]): ComuneIndex {
  const byIstat = new Map<string, ComuneFull>();
  const byProv = new Map<string, ComuneFull[]>();
  const byReg = new Map<string, ComuneFull[]>();
  const byDist = new Map<string, ComuneFull[]>();

  for (const c of comuni) {
    byIstat.set(c.istat, c);
    const provList = byProv.get(c.prov) ?? [];
    provList.push(c);
    byProv.set(c.prov, provList);
    // Код региона приходит в разной форме, и различает страны не формат, а то,
    // какие поля они несут:
    //   Италия      reg_code = 5 (int) и reg = «Veneto» (название)
    //   Испания     reg_code нет,       reg = «16»      (код codauto)
    //   Португалия  reg_code нет,       reg = «PT11»    (код NUTS 2)
    //
    // Поэтому reg_code приоритетнее, а reg берётся как есть, когда его нет.
    // Раньше здесь стояла проверка «reg годится, только если он из одних
    // цифр» — она отсекала название «Veneto», но заодно отсекла и буквенный
    // код NUTS, и индекс регионов Португалии молча оставался пустым: слой
    // верхнего уровня рисовался бы без данных, без единой ошибки.
    //
    // Формат кода тоже не общий: числовой нормализуется до двух знаков
    // («1» → «01», под reg_istat_code Италии), а буквенно-цифровой остаётся
    // ровно таким, каким лежит в TopoJSON.
    const regRaw = c.reg_code != null ? c.reg_code : c.reg;
    const regStr = regRaw == null ? '' : String(regRaw);
    const regKey = /^\d+$/.test(regStr) ? String(Number(regStr)).padStart(2, '0') : regStr;
    if (regKey) {
      const regList = byReg.get(regKey) ?? [];
      regList.push(c);
      byReg.set(regKey, regList);
    }
    // District (intermediate level). Comuni без поля dist просто не попадают
    // в индекс — district-слой для них пуст (graceful degradation).
    if (c.dist) {
      const distList = byDist.get(c.dist) ?? [];
      distList.push(c);
      byDist.set(c.dist, distList);
    }
  }

  return { byIstat, byProv, byReg, byDist };
}

export function buildLightIndex(comuni: ComuneLight[]): LightIndex {
  const byIstat = new Map<string, ComuneLight>();
  for (const c of comuni) byIstat.set(c.istat, c);
  return { byIstat };
}

// TopoJSON property → код единицы.
//
// Имя свойства приходит из реестра стран (MapAdminLevel.codeProp), потому что
// общего имени не существует: comuni_it.topojson несёт com_istat_code
// ('001001'), municipalities.topojson — ine ('03075'). Дефолты оставлены
// итальянскими, чтобы вызовы, которым страна неизвестна, вели себя ровно как
// раньше; всё, что работает с картой, обязано передавать имя явно.
export function topoIstat(properties: Record<string, unknown>, prop = 'com_istat_code'): string | null {
  const code = properties[prop];
  if (typeof code === 'string') return code;
  return null;
}

// provinces_it.topojson uses prov_acr like 'TO'; provinces.topojson (ES) — cpro '08'
export function topoProvAcr(properties: Record<string, unknown>, prop = 'prov_acr'): string | null {
  const acr = properties[prop];
  if (typeof acr === 'string') return acr;
  return null;
}

// districts_it.topojson uses dist_code like 'sll0404' / 'cc6537725'
export function topoDistCode(properties: Record<string, unknown>, prop = 'dist_code'): string | null {
  const code = properties[prop];
  if (typeof code === 'string') return code;
  return null;
}

// districts_it.topojson — отображаемое имя района (IT, с DE в скобках для cc)
export function topoDistName(properties: Record<string, unknown>): string | null {
  const it = properties['dist_name_it'];
  if (typeof it !== 'string') return null;
  const de = properties['dist_name_de'];
  if (typeof de === 'string' && de && de !== it) return `${it} / ${de}`;
  return it;
}

// regions_it.topojson uses reg_istat_code like '01' (zero-padded) + reg_name;
// ccaa.topojson (ES) — codauto '16' + name.
export function topoRegCode(properties: Record<string, unknown>, prop = 'reg_istat_code'): string | null {
  const code = properties[prop];
  if (typeof code === 'string') return code;
  return null;
}

export function topoRegName(properties: Record<string, unknown>, prop = 'reg_name'): string | null {
  const name = properties[prop];
  if (typeof name === 'string') return name;
  return null;
}

/** Человеческое имя единицы по имени свойства из реестра; null, если его нет. */
export function topoName(properties: Record<string, unknown>, prop: string | undefined): string | null {
  if (!prop) return null;
  const name = properties[prop];
  return typeof name === 'string' && name ? name : null;
}
